import React, { useEffect, useState, useCallback } from "react";
import TopNav from "@/components/TopNav";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import RejectDialog from "@/components/RejectDialog";
import LogoUploader from "@/components/LogoUploader";
import BulkFlagDetail from "@/components/BulkFlagDetail";
import CheckCell from "@/components/CheckCell";
import LicenseCell from "@/components/LicenseCell";
import TeamDialog from "@/components/TeamDialog";
import PlayerEditDialog from "@/components/PlayerEditDialog";
import ApprovalCriteria, { calcAge } from "@/components/ApprovalCriteria";
import { Progress } from "@/components/ui/progress";
import { useConfirm } from "@/lib/confirm";
import { toast } from "sonner";
import { Check, X, ExternalLink, Plus, Trash2, Mail, Calendar as CalendarIcon, AlertTriangle, Loader2, CheckCircle2, Pencil } from "lucide-react";

export default function AdminPanel() {
  const [tab, setTab] = useState("approvals");
  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <TopNav />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Admin</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter">Midtnorsk hockeyliga</h1>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-neutral-200 rounded-sm p-1">
            <TabsTrigger data-testid="tab-approvals" value="approvals" className="rounded-sm font-heading uppercase tracking-widest text-xs">Godkjenninger</TabsTrigger>
            <TabsTrigger data-testid="tab-matches" value="matches" className="rounded-sm font-heading uppercase tracking-widest text-xs">Kamper</TabsTrigger>
            <TabsTrigger data-testid="tab-teams" value="teams" className="rounded-sm font-heading uppercase tracking-widest text-xs">Lag</TabsTrigger>
            <TabsTrigger data-testid="tab-users" value="users" className="rounded-sm font-heading uppercase tracking-widest text-xs">Brukere</TabsTrigger>
            <TabsTrigger data-testid="tab-invitations" value="invitations" className="rounded-sm font-heading uppercase tracking-widest text-xs">Invitasjoner</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="approvals"><Approvals /></TabsContent>
            <TabsContent value="matches"><MatchesAdmin /></TabsContent>
            <TabsContent value="teams"><TeamsAdmin /></TabsContent>
            <TabsContent value="users"><UsersAdmin /></TabsContent>
            <TabsContent value="invitations"><InvitationsAdmin /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}

function Approvals() {
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [rejectFor, setRejectFor] = useState(null);
  const [bulkFlags, setBulkFlags] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, elapsed: 0 });
  const [flagDetail, setFlagDetail] = useState(null); // { name, flag }
  const [editPlayer, setEditPlayer] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pRes, tRes, mRes] = await Promise.all([
        api.get("/players", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/teams"),
        api.get("/matches"),
      ]);
      setPlayers(pRes.data);
      setTeams(tRes.data);
      setMatches(mRes.data);
      // also fetch all for counts
      const all = await api.get("/players");
      setAllPlayers(all.data);
    } catch (e) {
      toast.error("Kunne ikke laste");
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (p) => {
    try {
      await api.post(`/players/${p.id}/status`, { status: "approved" });
      toast.success("Godkjent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feil");
    }
  };

  const runBulkCheck = async () => {
    if (players.length === 0) return;
    setBulkLoading(true);
    const names = [...new Set(players.map((p) => p.full_name))];
    setBulkProgress({ done: 0, total: names.length, elapsed: 0 });
    const startedAt = Date.now();
    // simulated ticking so the user sees progress moving even while the single bulk POST runs
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      // assume average ~6s for first call, fast (<1s) when cached. Cap visible progress at 95% until done.
      const fraction = Math.min(0.95, elapsed / 8);
      setBulkProgress((p) => ({ ...p, done: Math.floor(fraction * p.total), elapsed }));
    }, 200);
    try {
      const res = await api.post("/lookup/players/bulk", { names });
      setBulkFlags(res.data);
      const hits = Object.values(res.data).filter((v) => v.hits > 0).length;
      if (hits === 0) toast.success(`Sjekket ${names.length} spillere — ingen nivåvarsler`);
      else toast.warning(`${hits} spiller${hits === 1 ? "" : "e"} flagget med NB Nivå`);
    } catch (e) {
      toast.error("Bulk-sjekk feilet — prøv igjen");
    } finally {
      clearInterval(tick);
      setBulkProgress({ done: names.length, total: names.length, elapsed: (Date.now() - startedAt) / 1000 });
      setBulkLoading(false);
    }
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";
  const matchTitle = (id) => {
    const m = matches.find((x) => x.id === id);
    return m ? `${m.title} (${new Date(m.match_date).toLocaleDateString("nb-NO")})` : "—";
  };

  const counts = {
    pending: allPlayers.filter((p) => p.status === "pending").length,
    approved: allPlayers.filter((p) => p.status === "approved").length,
    rejected: allPlayers.filter((p) => p.status === "rejected").length,
    sick: allPlayers.filter((p) => p.status === "sick").length,
    withdrawn: allPlayers.filter((p) => p.status === "withdrawn").length,
  };

  return (
    <div>
      <ApprovalCriteria />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { k: "pending", label: "Avventer", c: counts.pending },
          { k: "approved", label: "Godkjent", c: counts.approved },
          { k: "rejected", label: "Avvist", c: counts.rejected },
          { k: "sick", label: "Syk", c: counts.sick },
          { k: "withdrawn", label: "Frafall", c: counts.withdrawn },
        ].map((s) => (
          <button
            data-testid={`stat-${s.k}`}
            key={s.k}
            onClick={() => setFilter(s.k)}
            className={`swiss-card p-5 text-left transition ${filter === s.k ? "border-black ring-1 ring-black" : ""}`}
          >
            <div className="text-xs uppercase tracking-widest text-neutral-500">{s.label}</div>
            <div className="font-heading text-4xl font-black mt-1">{s.c}</div>
          </button>
        ))}
      </div>

      <div className="swiss-card">
        <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Spillere</div>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{filter === "all" ? "Alle" : filter === "pending" ? "Avventer godkjenning" : filter}</h2>
          </div>
          <div className="flex gap-2 items-center">
            <Button data-testid="bulk-check-btn" onClick={runBulkCheck} disabled={bulkLoading || players.length === 0} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest text-xs">
              {bulkLoading ? (<><Loader2 className="w-3 h-3 mr-2 animate-spin" />Sjekker mot hockey.no…</>) : `Sjekk alle (${players.length}) mot hockey.no`}
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setFilter("all")} data-testid="show-all-btn">Vis alle</Button>
          </div>
        </div>
        {bulkLoading && (
          <div className="px-6 pb-4 border-b border-neutral-200" data-testid="bulk-progress">
            <div className="flex items-center justify-between text-xs text-neutral-600 mb-2">
              <span className="uppercase tracking-widest font-heading">Sjekker mot live.hockey.no</span>
              <span className="font-mono">{bulkProgress.done}/{bulkProgress.total} · {bulkProgress.elapsed.toFixed(1)}s</span>
            </div>
            <Progress value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0} className="h-2" />
            <div className="text-xs text-neutral-500 mt-2">Henter sesongdata fra Eliteserien (kan ta 5-10 sek første gang, deretter raskt).</div>
          </div>
        )}
        {players.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">Ingen spillere her.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr className="text-left text-xs uppercase tracking-widest text-neutral-500">
                  <th className="px-6 py-3 w-16 text-center">Nr</th>
                  <th className="px-4 py-3">Navn</th>
                  <th className="px-4 py-3">Lag</th>
                  <th className="px-4 py-3">Kamp</th>
                  <th className="px-4 py-3">Posisjon</th>
                  <th className="px-4 py-3">Lisens</th>
                  <th className="px-4 py-3">Lisensstatus</th>
                  <th className="px-4 py-3">Sjekk</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Handling</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50 align-top">
                    <td className="px-6 py-4 text-center font-heading text-2xl font-black">{p.jersey_number}</td>
                    <td className="px-4 py-4">
                      <div className="font-semibold">{p.full_name}</div>
                      {p.date_of_birth && (() => {
                        const age = calcAge(p.date_of_birth);
                        return (
                          <div className="text-xs text-neutral-500">
                            f. {p.date_of_birth} {age !== null && <span className={age < 16 ? "text-rose-700 font-semibold" : ""}>({age} år{age < 16 ? " – under 16!" : ""})</span>}
                          </div>
                        );
                      })()}
                      <div className="text-xs text-neutral-500">{p.email}</div>
                      {p.rejection_reason && <div className="text-xs text-rose-700 mt-1">Avvist: {p.rejection_reason}</div>}
                    </td>
                    <td className="px-4 py-4">{teamName(p.team_id)}</td>
                    <td className="px-4 py-4 text-neutral-600">{matchTitle(p.match_id)}</td>
                    <td className="px-4 py-4">{p.position}</td>
                    <td className="px-4 py-4 font-mono text-xs">{p.nihf_license}</td>
                    <td className="px-4 py-4">
                      <LicenseCell player={p} isAdmin={true} onChanged={load} />
                    </td>
                    <td className="px-4 py-4">
                      <CheckCell player={p} flag={bulkFlags[p.full_name]} onShowDetail={setFlagDetail} testIdPrefix="" />
                    </td>
                    <td className="px-4 py-4"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-4">
                      {(() => {
                        const age = calcAge(p.date_of_birth);
                        const underage = age !== null && age < 16;
                        return (
                          <div className="flex justify-end gap-2">
                            <Button data-testid={`edit-player-${p.id}`} size="sm" variant="outline" className="rounded-sm" onClick={() => setEditPlayer(p)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {p.status !== "approved" && (
                              underage ? (
                                <Button data-testid={`approve-blocked-${p.id}`} size="sm" disabled title="Under 16 år — kan ikke godkjennes" className="bg-neutral-300 text-neutral-600 rounded-sm cursor-not-allowed">
                                  <Check className="w-4 h-4 mr-1" />Blokkert ({age}å)
                                </Button>
                              ) : (
                                <Button data-testid={`approve-${p.id}`} size="sm" onClick={() => approve(p)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
                                  <Check className="w-4 h-4 mr-1" />Godkjenn
                                </Button>
                              )
                            )}
                            {p.status !== "rejected" && (
                              <Button data-testid={`reject-${p.id}`} size="sm" onClick={() => setRejectFor(p)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm">
                                <X className="w-4 h-4 mr-1" />Avvis
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rejectFor && (
        <RejectDialog player={rejectFor} open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)} onDone={load} />
      )}
      <BulkFlagDetail
        open={!!flagDetail}
        onOpenChange={(v) => !v && setFlagDetail(null)}
        playerName={flagDetail?.name}
        flag={flagDetail?.flag}
      />
      <PlayerEditDialog
        open={!!editPlayer}
        onOpenChange={(v) => !v && setEditPlayer(null)}
        player={editPlayer}
        onSaved={load}
      />
    </div>
  );
}

function MatchesAdmin() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const [m, t] = await Promise.all([api.get("/matches"), api.get("/teams/all")]);
    setMatches(m.data);
    setTeams(t.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!(await confirm({ title: "Slette kamp?", description: "Kampen og resultatet fjernes permanent.", confirmLabel: "Slett", destructive: true }))) return;
    await api.delete(`/matches/${id}`);
    toast.success("Slettet");
    load();
  };

  return (
    <div className="swiss-card">
      <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Kamper</div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{matches.length} kamper</h2>
        </div>
        <Button data-testid="new-match-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">
          <Plus className="w-4 h-4 mr-1" />Ny kamp
        </Button>
      </div>
      {matches.length === 0 ? (
        <div className="p-12 text-center text-neutral-500">Ingen kamper opprettet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-xs uppercase tracking-widest text-neutral-500">
                <th className="px-6 py-3">Kamp</th>
                <th className="px-4 py-3">Dato</th>
                <th className="px-4 py-3">Hjemmelag</th>
                <th className="px-4 py-3">Bortelag</th>
                <th className="px-4 py-3">Resultat</th>
                <th className="px-4 py-3">Arena</th>
                <th className="px-4 py-3 text-right">Handling</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => {
                const homeName = teams.find((t) => t.id === m.home_team_id)?.name || "—";
                const awayName = teams.find((t) => t.id === m.away_team_id)?.name || m.away_team || "—";
                const played = m.home_score != null && m.away_score != null;
                return (
                  <tr key={m.id} className="border-b border-neutral-100 hover:bg-neutral-50 align-top">
                    <td className="px-6 py-4 font-semibold">{m.title}</td>
                    <td className="px-4 py-4">{new Date(m.match_date).toLocaleString("nb-NO", { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="px-4 py-4">{homeName}</td>
                    <td className="px-4 py-4">{awayName}</td>
                    <td className="px-4 py-4">
                      {played ? (
                        <div className="font-heading font-black text-base">
                          {m.home_score} – {m.away_score}
                          {m.overtime && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-700">OT</span>}
                        </div>
                      ) : (
                        <span className="text-xs uppercase tracking-widest text-neutral-400">Ikke spilt</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-neutral-600">{m.venue || "—"}</td>
                    <td className="px-4 py-4 text-right">
                      <Button data-testid={`edit-match-${m.id}`} size="sm" variant="outline" className="rounded-sm mr-2" onClick={() => { setEditing(m); setOpen(true); }}>Rediger</Button>
                      <Button data-testid={`del-match-${m.id}`} size="sm" variant="ghost" onClick={() => remove(m.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <MatchDialog open={open} onOpenChange={setOpen} match={editing} teams={teams} onSaved={load} />
    </div>
  );
}

function MatchDialog({ open, onOpenChange, match, teams, onSaved }) {
  const [form, setForm] = useState({ title: "", home_team_id: "", away_team_id: "", away_team: "", venue: "", match_date: "", organizer_logo_path: null, home_score: "", away_score: "", overtime: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (match) {
      setForm({
        title: match.title || "",
        home_team_id: match.home_team_id || "",
        away_team_id: match.away_team_id || "",
        away_team: match.away_team || "",
        venue: match.venue || "",
        match_date: match.match_date ? match.match_date.slice(0, 16) : "",
        organizer_logo_path: match.organizer_logo_path || null,
        home_score: match.home_score ?? "",
        away_score: match.away_score ?? "",
        overtime: !!match.overtime,
      });
    } else {
      setForm({ title: "", home_team_id: "", away_team_id: "", away_team: "", venue: "", match_date: "", organizer_logo_path: null, home_score: "", away_score: "", overtime: false });
    }
  }, [match, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title || !form.match_date) { toast.error("Tittel og dato kreves"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        match_date: new Date(form.match_date).toISOString(),
        home_score: form.home_score === "" ? null : Number(form.home_score),
        away_score: form.away_score === "" ? null : Number(form.away_score),
      };
      if (match) await api.patch(`/matches/${match.id}`, payload);
      else await api.post("/matches", payload);
      toast.success(match ? "Oppdatert" : "Opprettet");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Feil ved lagring");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{match ? "Rediger kamp" : "Ny kamp"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Kamptittel</Label>
            <Input data-testid="match-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="F.eks. Runde 3" className="rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Dato & klokkeslett</Label>
            <Input data-testid="match-date" type="datetime-local" value={form.match_date} onChange={(e) => set("match_date", e.target.value)} className="rounded-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Hjemmelag</Label>
              <Select value={form.home_team_id} onValueChange={(v) => set("home_team_id", v)}>
                <SelectTrigger data-testid="match-home" className="rounded-sm"><SelectValue placeholder="Velg lag" /></SelectTrigger>
                <SelectContent>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Bortelag</Label>
              <Select value={form.away_team_id} onValueChange={(v) => { set("away_team_id", v); set("away_team", ""); }}>
                <SelectTrigger data-testid="match-away-id" className="rounded-sm"><SelectValue placeholder="Velg fra liste" /></SelectTrigger>
                <SelectContent>
                  {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!form.away_team_id && (
                <Input data-testid="match-away" value={form.away_team} onChange={(e) => set("away_team", e.target.value)} placeholder="...eller eksternt lagnavn" className="rounded-sm mt-2" />
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Arena</Label>
            <Input data-testid="match-venue" value={form.venue} onChange={(e) => set("venue", e.target.value)} className="rounded-sm" />
          </div>

          <div className="border-t border-neutral-200 pt-4">
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Resultat (etter spilt kamp)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Hjemme mål</Label>
                <Input data-testid="match-home-score" type="number" min="0" value={form.home_score} onChange={(e) => set("home_score", e.target.value)} className="rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Borte mål</Label>
                <Input data-testid="match-away-score" type="number" min="0" value={form.away_score} onChange={(e) => set("away_score", e.target.value)} className="rounded-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
              <input data-testid="match-ot" type="checkbox" checked={form.overtime} onChange={(e) => set("overtime", e.target.checked)} className="rounded-sm" />
              <span>Avgjort etter OT / straffer (taper får 1 poeng)</span>
            </label>
            <p className="text-xs text-neutral-500 mt-2">La feltene stå tomme inntil kampen er spilt.</p>
          </div>

          <LogoUploader value={form.organizer_logo_path} onChange={(v) => set("organizer_logo_path", v)} label="Arrangør-logo" testid="match-logo" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Avbryt</Button>
          <Button data-testid="save-match-btn" disabled={submitting} onClick={save} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">{submitting ? "Lagrer…" : "Lagre"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamsAdmin() {
  const [teams, setTeams] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const r = await api.get("/teams");
    setTeams(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id, name) => {
    let impact = { players: 0, matches: 0 };
    try {
      const r = await api.get(`/teams/${id}/impact`);
      impact = r.data;
    } catch (e) {
      // proceed even if impact fetch fails
    }
    const parts = [];
    if (impact.players) parts.push(`${impact.players} spiller${impact.players === 1 ? "" : "e"}`);
    if (impact.matches) parts.push(`${impact.matches} kamp${impact.matches === 1 ? "" : "er"}`);
    const desc = parts.length
      ? `Dette vil også slette ${parts.join(" og ")} permanent.`
      : `Laget ${name || ""} slettes permanent.`;
    if (!(await confirm({ title: "Slette lag?", description: desc, confirmLabel: "Slett", destructive: true }))) return;
    try {
      const res = await api.delete(`/teams/${id}`);
      const d = res.data || {};
      const extra = [];
      if (d.players_deleted) extra.push(`${d.players_deleted} spillere`);
      if (d.matches_deleted) extra.push(`${d.matches_deleted} kamper`);
      toast.success(extra.length ? `Lag slettet (${extra.join(", ")})` : "Lag slettet");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Ukjent feil";
      toast.error(`Kunne ikke slette lag: ${msg}`);
    }
  };

  return (
    <div className="swiss-card">
      <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Lag</div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{teams.length} lag</h2>
        </div>
        <Button data-testid="new-team-admin-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">
          <Plus className="w-4 h-4 mr-1" />Nytt lag
        </Button>
      </div>
      <div className="divide-y divide-neutral-100">
        {teams.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                {t.logo_path ? <img src={fileUrl(t.logo_path)} alt={t.name} className="w-full h-full object-contain" /> : <span className="font-heading font-black text-neutral-400">{t.name[0]}</span>}
              </div>
              <div>
                <div className="font-semibold">{t.name}</div>
                <div className="text-xs text-neutral-500 font-mono">{t.accent_color}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-sm border border-neutral-200" style={{ backgroundColor: t.accent_color }} />
              <Button data-testid={`edit-team-${t.id}`} size="sm" variant="outline" className="rounded-sm" onClick={() => { setEditing(t); setDialogOpen(true); }}>
                <Pencil className="w-4 h-4 mr-1" />Rediger
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(t.id, t.name)} data-testid={`del-team-${t.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
            </div>
          </div>
        ))}
        {teams.length === 0 && <div className="p-12 text-center text-neutral-500">Ingen lag enda. Lagledere oppretter selv lag fra dashboardet, eller du kan opprette et her.</div>}
      </div>
      <TeamDialog open={dialogOpen} onOpenChange={setDialogOpen} team={editing} onSaved={load} />
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    await api.post(`/users/${u.user_id}/role`, null, { params: { role } });
    toast.success("Rolle oppdatert");
    load();
  };

  return (
    <div className="swiss-card">
      <div className="p-6 border-b border-neutral-200">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Brukere</div>
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{users.length} brukere</h2>
      </div>
      <div className="divide-y divide-neutral-100">
        {users.map((u) => (
          <div key={u.user_id} className="flex items-center justify-between p-5">
            <div className="flex items-center gap-4">
              {u.picture && <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />}
              <div>
                <div className="font-semibold">{u.name}</div>
                <div className="text-xs text-neutral-500">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs uppercase font-heading tracking-widest px-2 py-1 ${u.role === "admin" ? "tag-approved" : "tag-pending"}`}>{u.role}</span>
              {u.role === "admin" ? (
                <Button size="sm" variant="outline" onClick={() => setRole(u, "lagleder")} className="rounded-sm" data-testid={`demote-${u.user_id}`}>Sett som lagleder</Button>
              ) : (
                <Button size="sm" onClick={() => setRole(u, "admin")} className="bg-black text-white hover:bg-neutral-800 rounded-sm" data-testid={`promote-${u.user_id}`}>Gjør til admin</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function InvitationsAdmin() {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("lagleder");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    const r = await api.get("/invitations");
    setInvites(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/invitations", { email, role, note: note || null });
      toast.success("Invitasjon opprettet");
      setEmail(""); setNote(""); setRole("lagleder");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feil");
    } finally {
      setSubmitting(false);
    }
  };

  const updateRole = async (inv, newRole) => {
    await api.patch(`/invitations/${inv.id}`, { role: newRole });
    toast.success("Rolle oppdatert");
    load();
  };

  const setStatus = async (inv, status) => {
    await api.patch(`/invitations/${inv.id}`, { status });
    toast.success(status === "revoked" ? "Trukket tilbake" : "Reaktivert");
    load();
  };

  const remove = async (inv) => {
    if (!(await confirm({ title: `Slette invitasjon for ${inv.email}?`, confirmLabel: "Slett", destructive: true }))) return;
    await api.delete(`/invitations/${inv.id}`);
    toast.success("Slettet");
    load();
  };

  return (
    <div className="space-y-6">
      <form onSubmit={create} className="swiss-card p-6">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Ny invitasjon</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Epost</Label>
            <Input data-testid="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" placeholder="lagleder@klubb.no" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Rolle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lagleder">Lagleder</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Notat (valgfritt)</Label>
            <Input data-testid="invite-note" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm" placeholder="F.eks. Storhamar U18" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="invite-submit" type="submit" disabled={submitting} className="bg-black text-white hover:bg-neutral-800 rounded-sm font-heading uppercase tracking-widest">
            {submitting ? "Lagrer…" : "Inviter"}
          </Button>
        </div>
        <p className="text-xs text-neutral-500 mt-3">Den inviterte logger inn med Google på samme epost. Personer uten invitasjon avvises automatisk.</p>
      </form>

      <div className="swiss-card">
        <div className="p-6 border-b border-neutral-200">
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Invitasjoner</div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{invites.length} totalt</h2>
        </div>
        {invites.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">Ingen invitasjoner ennå.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr className="text-left text-xs uppercase tracking-widest text-neutral-500">
                  <th className="px-6 py-3">Epost</th>
                  <th className="px-4 py-3">Rolle</th>
                  <th className="px-4 py-3">Notat</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Handling</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-6 py-4 font-semibold">{inv.email}</td>
                    <td className="px-4 py-4">
                      <Select value={inv.role} onValueChange={(v) => updateRole(inv, v)} disabled={inv.status !== "active"}>
                        <SelectTrigger className="rounded-sm h-9 w-36" data-testid={`role-select-${inv.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lagleder">Lagleder</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-4 text-neutral-600">{inv.note || "—"}</td>
                    <td className="px-4 py-4">
                      <span className={`text-xs uppercase font-heading tracking-widest px-2 py-1 ${inv.status === "active" ? "tag-pending" : inv.status === "used" ? "tag-approved" : "tag-withdrawn"}`}>
                        {inv.status === "active" ? "Aktiv" : inv.status === "used" ? "Brukt" : "Trukket"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      {inv.status === "active" && (
                        <Button data-testid={`revoke-${inv.id}`} size="sm" variant="outline" className="rounded-sm mr-2" onClick={() => setStatus(inv, "revoked")}>Trekk tilbake</Button>
                      )}
                      {inv.status === "revoked" && (
                        <Button data-testid={`reactivate-${inv.id}`} size="sm" variant="outline" className="rounded-sm mr-2" onClick={() => setStatus(inv, "active")}>Reaktiver</Button>
                      )}
                      <Button data-testid={`del-invite-${inv.id}`} size="sm" variant="ghost" onClick={() => remove(inv)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
